import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import echobuddyLogo from "../../assets/newEchoLogo.png";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: '0 12px 40px rgba(16, 24, 40, 0.08)',
  border: '1px solid #E5E7EB',
  backgroundColor: '#ffffff'
}));

const SourceCard = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  border: '1px solid #E5E7EB',
  backgroundColor: '#FAFBFF',
  padding: '24px 28px',
  marginBottom: 20
}));

const SECTIONS = {
  a: '#38BDF8',
  b: '#818CF8',
  c: '#2CC795',
  d: '#F9BE4A',
  e: '#FB923C'
};

const AboutThisReport = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
      {/* Header — same pattern used across the app */}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, sm: 4, md: 6 },
          py: 1,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderBottom: '1px solid #E5E7EB'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src={echobuddyLogo}
              alt="EchoBuddy"
              style={{ height: 45, width: 45, borderRadius: '50%', objectFit: 'cover', marginTop: '0.1rem' }}
            />
            <Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.5px', color: '#7843f3', lineHeight: 1.1, mb: 0.2 }}>
                echoShine
              </Typography>
              <Typography variant="caption" sx={{ color: '#7843f3', fontSize: '0.65rem', fontWeight: 500, letterSpacing: '0.3px', display: 'block', lineHeight: 1.2 }}>
                Connect. Heal. Rise.
              </Typography>
            </Box>
          </Link>
        </Box>

        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            onClick={() => navigate(-1)}
            sx={{
              background: 'none',
              border: 'none',
              color: '#3B3FA0',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '15px',
              padding: 0,
              minWidth: 'auto'
            }}
          >
            ‹ Back
          </Button>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ maxWidth: 800, mx: 'auto', py: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C2F6B', mb: 3 }}>
          About This Report
        </Typography>

        {/* Introduction */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent sx={{ p: isMobile ? 3 : 4.5 }}>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              Your Wellbeing Report provides a summary of your self-reported wellbeing based on the answers you provided in the wellbeing questionnaire.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              The report is designed to help you reflect on different areas of your wellbeing and educational experience.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
              Your results represent your responses at the time the questionnaire was completed and may change over time.
            </Typography>
          </CardContent>
        </StyledCard>

        {/* How Calculated */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent sx={{ p: isMobile ? 3 : 4.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C2F6B', mb: 2 }}>
              How Your Report Is Calculated
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              Your Wellbeing Report uses questionnaire responses to calculate scores across different areas of wellbeing.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              <strong style={{ color: SECTIONS.a }}>Life Satisfaction</strong> is scored from <strong>5 to 35</strong>.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 1 }}>
              The student wellbeing assessment measures four areas:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" sx={{ lineHeight: 1.85, fontSize: '16px', color: SECTIONS.b }}>Joy of Learning</Typography>
              <Typography component="li" sx={{ lineHeight: 1.85, fontSize: '16px', color: SECTIONS.c }}>Campus Connectedness</Typography>
              <Typography component="li" sx={{ lineHeight: 1.85, fontSize: '16px', color: SECTIONS.d }}>Educational Purpose</Typography>
              <Typography component="li" sx={{ lineHeight: 1.85, fontSize: '16px', color: SECTIONS.e }}>Academic Efficacy</Typography>
            </Box>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              Each of these areas is scored from <strong>4 to 16</strong>.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              The four student wellbeing scores are combined to provide an overall student wellbeing score ranging from <strong>16 to 64</strong>.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B', mb: 2 }}>
              Higher scores generally indicate more frequent positive wellbeing experiences.
            </Typography>
            <Typography sx={{ lineHeight: 1.85, fontSize: '16px', color: '#2C2F6B' }}>
              The score labels shown in the report are intended to help users understand their questionnaire responses and should not be interpreted as a medical or psychological diagnosis.
            </Typography>
          </CardContent>
        </StyledCard>

        {/* Assessment Sources */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C2F6B', mb: 2, mt: 4 }}>
          Assessment Sources
        </Typography>

        <SourceCard>
          <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#2C2F6B', mb: 1 }}>
            Satisfaction With Life Scale (SWLS)
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#4B5170', mb: 2 }}>
            The Life Satisfaction score is based on the Satisfaction With Life Scale, a five-item questionnaire designed to measure a person's overall satisfaction with life.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '14px', color: '#7B8DB0', mb: 1 }}>
            Diener, E., Emmons, R. A., Larsen, R. J., &amp; Griffin, S. (1985). The Satisfaction With Life Scale. Journal of Personality Assessment, 49(1), 71–75.
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#7B8DB0', mb: 2 }}>
            DOI: 10.1207/s15327752jpa4901_13
          </Typography>
          <Button
            component="a"
            href="https://doi.org/10.1207/s15327752jpa4901_13"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#3B3FA0',
              color: '#3B3FA0',
              fontWeight: 600,
              '&:hover': { borderColor: '#3B3FA0', backgroundColor: 'rgba(59,63,160,0.05)' }
            }}
          >
            View Source
          </Button>
        </SourceCard>

        <SourceCard>
          <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#2C2F6B', mb: 1 }}>
            Student Subjective Wellbeing Questionnaire (SSWQ)
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#4B5170', mb: 1 }}>
            The student wellbeing portion of the report is based on the Student Subjective Wellbeing Questionnaire.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#4B5170', mb: 1 }}>
            The questionnaire measures four areas of student wellbeing: Joy of Learning, School Connectedness, Educational Purpose, and Academic Efficacy.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#4B5170', mb: 2 }}>
            In our application, "School Connectedness" is displayed as "Campus Connectedness" to better reflect the higher-education environment.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '14px', color: '#7B8DB0', mb: 1 }}>
            Renshaw, T. L., Long, A. C. J., &amp; Cook, C. R. (2015). Assessing adolescents' positive psychological functioning at school: Development and validation of the Student Subjective Wellbeing Questionnaire. School Psychology Quarterly, 30(4), 534–552.
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#7B8DB0', mb: 2 }}>
            DOI: 10.1037/spq0000088
          </Typography>
          <Button
            component="a"
            href="https://doi.org/10.1037/spq0000088"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#3B3FA0',
              color: '#3B3FA0',
              fontWeight: 600,
              '&:hover': { borderColor: '#3B3FA0', backgroundColor: 'rgba(59,63,160,0.05)' }
            }}
          >
            View Source
          </Button>
        </SourceCard>

        {/* Important Information */}
        <Box
          sx={{
            mt: 4,
            mb: 3,
            p: isMobile ? 3 : 4,
            borderRadius: '16px',
            backgroundColor: '#FFF8E6',
            border: '1px solid #F5E3A8'
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#8A6D1D', mb: 1.5, fontSize: '16px' }}>
            Important Information
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#6B5A20', mb: 1.5 }}>
            This Wellbeing Report is provided for informational, educational, and wellbeing-awareness purposes only.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#6B5A20', mb: 1.5 }}>
            It is not intended to diagnose, treat, cure, or prevent any medical or mental health condition.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#6B5A20', mb: 1.5 }}>
            The report should not be considered medical advice, psychological advice, or a substitute for professional healthcare advice.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#6B5A20', mb: 1.5 }}>
            Do not use the results as the sole basis for making medical or mental-health decisions.
          </Typography>
          <Typography sx={{ lineHeight: 1.8, fontSize: '15px', color: '#6B5A20' }}>
            If you have concerns about your physical or mental health, please consult a qualified healthcare professional, counsellor, psychologist, or doctor.
          </Typography>
        </Box>

        {/* Bottom note */}
        <Typography sx={{ fontSize: '13px', color: '#A0AECB', textAlign: 'center', mt: 3 }}>
          Assessment results are based on self-reported questionnaire responses.
        </Typography>
      </Container>
    </Box>
  );
};

export default AboutThisReport;
